import type { Timezone } from '@bb/domain/dist/types';
import { breakpoints } from 'components/theme';
import { utcToZonedTime, format, zonedTimeToUtc } from 'date-fns-tz';
import {
  createFormatDateFunction,
  formatISODateStr,
  getStartOfDayInTimezone,
} from 'utils';
import type { TourItemContent } from './types';
import type { ESTourItemResponse } from '../MyBusinessPage/types';

export const isWaypointOptimized = (waypointOrder?: number[]) => {
  // Should not be reached
  if (!waypointOrder) return true;

  return waypointOrder.every((order, index) => order === index);
};

export const reorderOptimizedTour = (
  tourItems: TourItemContent[],
  waypointOrder?: number[],
  finalDestinationIndex?: number,
) => {
  // ForEach will have better perf, but only 25 items max in array
  const withCoords = tourItems.filter(item => Boolean(item.coordinates));
  // Should not be reached
  if (!waypointOrder || withCoords.length < 3) return tourItems;

  // Move destination to the last position
  if (finalDestinationIndex) {
    const destination = withCoords[finalDestinationIndex];
    withCoords.splice(finalDestinationIndex, 1);
    withCoords.push(destination);
  }

  const wayPoints = withCoords.slice(1, -1);
  const newWayPoints: TourItemContent[] = [];
  waypointOrder.forEach(order => {
    newWayPoints.push(wayPoints[order]);
  });
  const reorderedItems = [
    withCoords[0],
    ...newWayPoints,
    withCoords[withCoords.length - 1],
  ];

  if (!tourItems.some(item => !item.coordinates)) return reorderedItems;
  // If there are some items without coordinates, place them in original position
  tourItems.forEach((item, index) => {
    if (item.coordinates) return;
    reorderedItems.splice(index, 0, item);
  });
  return reorderedItems;
};

export const formatToZonedTime = (
  time: Date,
  timeFormat: string,
  timezone: Timezone,
) => format(utcToZonedTime(time, timezone), timeFormat, {
    timeZone: timezone,
  });

export const timeFormatter = (time: Date, timezone: Timezone) => {
  const formatter = createFormatDateFunction('h:mm aaa');
  return formatter(time, timezone).replace(' ', '');
};

export const checkIsIOS = () => navigator.userAgent.match(/iPhone|iPad|iPod/i);

export const buildItemCountText = (count: number): string => `${count} stop${(count !== 1 && 's') || ''}`;
// return the date string used for the tour item
export const getTourItemDateToShow = (
  {
    status,
    listingContactTimeChange,
    date,
    originalDate,
    time,
    originalTime,
  }: Partial<ESTourItemResponse>,
  timezone?: Timezone,
): string => {
  // if no timezone is given, use date field,
  // otherwise try to use time field.
  const dateToUse =
    status === 'suggested_time_change' ||
    (status === 'time_change' && listingContactTimeChange)
      ? (timezone && originalTime) || originalDate
      : (timezone && time) || date;

  return timezone ? formatISODateStr(dateToUse, timezone) : dateToUse;
};

export const renderTourItemApprovalTypeLabel = (
  tourItem: TourItemContent,
): string => {
  if (tourItem.listingAgentConfirm) return `Listing Agent to Confirm`;
  if (!tourItem.autoConfirm) return `Seller / Contact to Confirm`;
  return ``;
};

export { getStartOfDayInTimezone };
